import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";

const AREAS = ["North","South","East","West","Central"]; // legacy, unused for managers now
const MANAGER_TIERS = [
  { value: "asm", label: "Area Sales Manager" },
  { value: "kam", label: "Key Account Manager" },
  { value: "sales_head", label: "Sales Head" },
  { value: "director", label: "Director" },
];
const ROLES = ["admin","dealer","sales","manager","accounts"];

const ROLE_STYLE = {
  admin:  "bg-rose-50 text-rose-700 border-rose-200",
  dealer: "bg-blue-50 text-blue-700 border-blue-200",
  sales:  "bg-purple-50 text-purple-700 border-purple-200",
  manager:"bg-amber-50 text-amber-800 border-amber-200",
  accounts:"bg-emerald-50 text-emerald-700 border-emerald-200",
};

const empty = { name: "", email: "", password: "", role: "sales", area: "", dealer_id: "", assigned_address_ids: [], manager_tier: "", assigned_user_ids: [] };

// Flatten every dealer's addresses into one list of individually-assignable
// locations — a sales rep is scoped to specific locations, not whole dealers.
// Billing addresses are excluded: they're finance/admin records, not a place
// a sales rep "owns" — only shipping (or single combined) locations count.
function flattenAddresses(dealers) {
  return dealers.flatMap((d) =>
    (d.addresses || [])
      .filter((a) => a.kind !== "billing")
      .map((a) => ({ ...a, dealerId: d.dealer_id, dealerName: d.name, dealerCode: d.code }))
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [assignIds, setAssignIds] = useState([]);
  const [locQuery, setLocQuery] = useState("");
  const [managerFor, setManagerFor] = useState(null);
  const [managerTier, setManagerTier] = useState("");
  const [managerSubIds, setManagerSubIds] = useState([]);

  const load = () => Promise.all([
    api.get("/users").then(({data}) => setUsers(data)),
    api.get("/dealers").then(({data}) => setDealers(data)),
  ]);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.role === "manager" && !form.manager_tier) {
      return toast.error("Pick a manager tier");
    }
    setBusy(true);
    try {
      const payload = { ...form, email: form.email.toLowerCase() };
      if (payload.role !== "manager") { delete payload.area; delete payload.manager_tier; delete payload.assigned_user_ids; }
      if (payload.role === "manager" && payload.manager_tier === "director") delete payload.assigned_user_ids;
      if (payload.role !== "dealer") delete payload.dealer_id;
      if (payload.role !== "sales") delete payload.assigned_address_ids;
      await api.post("/users", payload);
      toast.success(`Created ${payload.email}`);
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const update = async (user_id, patch) => {
    try { await api.patch(`/users/${user_id}`, patch); load(); toast.success("Updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (u) => {
    if (!confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.user_id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Password must be 6+ chars");
    try { await api.patch(`/users/${pwFor.user_id}`, { password: newPw }); toast.success("Password reset"); setPwFor(null); setNewPw(""); }
    catch (e) { toast.error("Failed"); }
  };
  const saveAssign = async () => {
    try {
      await update(assignFor.user_id, { assigned_address_ids: assignIds });
      setAssignFor(null);
    } catch (e) { toast.error("Failed"); }
  };

  const saveManager = async () => {
    if (!managerTier) return toast.error("Pick a manager tier");
    try {
      await update(managerFor.user_id, {
        manager_tier: managerTier,
        assigned_user_ids: managerTier === "director" ? [] : managerSubIds,
      });
      setManagerFor(null);
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Access control</div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Users & Roles</h1>
          <p className="text-sm text-slate-600 mt-1">Only the Bhawar admin can create accounts and assign roles.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="add-user-btn"><UserPlus size={16} className="mr-2"/>Add user</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create a new user</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Full name</Label>
                <Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="user-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="user-email" />
              </div>
              <div>
                <Label>Temporary password</Label>
                <Input type="text" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} data-testid="user-password" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v=>setForm({...form, role: v})}>
                  <SelectTrigger data-testid="user-role"><SelectValue className="capitalize" /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.role === "manager" && (
                <div className="col-span-2 space-y-3">
                  <div>
                    <Label>Manager tier</Label>
                    <Select value={form.manager_tier} onValueChange={v=>setForm({...form, manager_tier: v})}>
                      <SelectTrigger data-testid="user-manager-tier"><SelectValue placeholder="Pick tier" /></SelectTrigger>
                      <SelectContent>
                        {MANAGER_TIERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.manager_tier && form.manager_tier !== "director" && (
                    <div>
                      <Label>Subordinates</Label>
                      <p className="text-xs text-slate-500 mb-1">This manager can see and approve orders from these people (and, transitively, anyone under a manager listed here).</p>
                      <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                        {users.filter(u => u.role === "sales" || u.role === "manager").map(u => {
                          const checked = form.assigned_user_ids.includes(u.user_id);
                          return (
                            <label key={u.user_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(v) => {
                                  const next = v
                                    ? [...form.assigned_user_ids, u.user_id]
                                    : form.assigned_user_ids.filter(id => id !== u.user_id);
                                  setForm({ ...form, assigned_user_ids: next });
                                }}
                              />
                              <span className="capitalize text-slate-500">{u.role}</span> · {u.name} ({u.email})
                            </label>
                          );
                        })}
                        {users.filter(u => u.role === "sales" || u.role === "manager").length === 0 && (
                          <div className="px-3 py-4 text-xs text-slate-400 text-center">No sales or manager users to assign yet — create those first.</div>
                        )}
                      </div>
                    </div>
                  )}
                  {form.manager_tier === "director" && (
                    <p className="text-xs text-slate-500">Directors see every dealer and PO — no subordinate assignment needed.</p>
                  )}
                </div>
              )}
              {form.role === "dealer" && (
                <div className="col-span-2">
                  <Label>Dealer account</Label>
                  <Select value={form.dealer_id} onValueChange={v=>setForm({...form, dealer_id: v})}>
                    <SelectTrigger data-testid="user-dealer"><SelectValue placeholder="Pick dealer" /></SelectTrigger>
                    <SelectContent>{dealers.map(d => <SelectItem key={d.dealer_id} value={d.dealer_id}>{d.name} · {d.code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.role === "sales" && (
                <div className="col-span-2">
                  <Label>Assigned locations</Label>
                  <p className="text-xs text-slate-500 mb-1">This sales rep can only place orders for the specific dealer locations checked below.</p>
                  <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                    {flattenAddresses(dealers).map(a => {
                      const checked = form.assigned_address_ids.includes(a.id);
                      return (
                        <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const next = v
                                ? [...form.assigned_address_ids, a.id]
                                : form.assigned_address_ids.filter(id => id !== a.id);
                              setForm({ ...form, assigned_address_ids: next });
                            }}
                          />
                          <span>{a.dealerName} · {a.dealerCode} · <span className="text-slate-500">{a.label} — {a.city}{a.area ? ` (${a.area})` : ""}</span></span>
                        </label>
                      );
                    })}
                    {flattenAddresses(dealers).length === 0 && (
                      <div className="px-3 py-4 text-xs text-slate-400 text-center">No dealer locations yet — import dealers via SAP Sync first.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="save-user-btn">
                <Plus size={14} className="mr-1" /> Create user
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name & email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Scope</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map(u => (
              <tr key={u.user_id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={v => update(u.user_id, { role: v })}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`role-select-${u.email}`}>
                      <SelectValue className="capitalize" />
                    </SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {u.role === "manager" && (
                    <button
                      className="underline decoration-dotted hover:text-slate-900 text-left"
                      onClick={() => { setManagerFor(u); setManagerTier(u.manager_tier || ""); setManagerSubIds(u.assigned_user_ids || []); }}
                      data-testid={`edit-manager-${u.email}`}
                    >
                      {MANAGER_TIERS.find(t => t.value === u.manager_tier)?.label || "No tier set"}
                      {u.manager_tier && u.manager_tier !== "director" && ` · ${(u.assigned_user_ids || []).length} people`}
                      {" — edit"}
                    </button>
                  )}
                  {u.role === "dealer" && (dealers.find(d => d.dealer_id === u.dealer_id)?.name || "—")}
                  {["admin","accounts"].includes(u.role) && "All"}
                  {u.role === "sales" && (
                    <button
                      className="underline decoration-dotted hover:text-slate-900"
                      onClick={() => { setAssignFor(u); setAssignIds(u.assigned_address_ids || []); }}
                      data-testid={`edit-assigned-${u.email}`}
                    >
                      {(u.assigned_address_ids || []).length} location{(u.assigned_address_ids || []).length === 1 ? "" : "s"} — edit
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Switch checked={u.active !== false} onCheckedChange={v => update(u.user_id, { active: v })} data-testid={`active-toggle-${u.email}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setPwFor(u)} data-testid={`reset-pw-${u.email}`}><KeyRound size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(u)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-user-${u.email}`}><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <Dialog open={!!pwFor} onOpenChange={o=>{ if(!o){setPwFor(null); setNewPw("");} }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {pwFor?.email}</DialogTitle></DialogHeader>
          <Input placeholder="New password (min 6 chars)" value={newPw} onChange={e=>setNewPw(e.target.value)} data-testid="new-pw-input" />
          <DialogFooter><Button onClick={resetPw} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="confirm-pw-btn">Set password</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignFor} onOpenChange={o=>{ if(!o) { setAssignFor(null); setLocQuery(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assigned locations — {assignFor?.email}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">This sales rep can only place orders for the specific dealer locations checked below.</p>
          <Input placeholder="Search address, dealer, city..." value={locQuery} onChange={(e) => setLocQuery(e.target.value)} />
          <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
            {flattenAddresses(dealers)
              .filter(a => {
                const s = `${a.dealerName} ${a.dealerCode} ${a.label} ${a.line1} ${a.city} ${a.state} ${a.pincode}`.toLowerCase();
                return !locQuery || s.includes(locQuery.toLowerCase());
              })
              .map(a => {
              const checked = assignIds.includes(a.id);
              return (
                <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => {
                      setAssignIds(v ? [...assignIds, a.id] : assignIds.filter(id => id !== a.id));
                    }}
                  />
                  <span>{a.dealerName} · {a.dealerCode} · <span className="text-slate-500">{a.label} — {a.line1}, {a.city}, {a.state} - {a.pincode}</span></span>
                </label>
              );
            })}
            {flattenAddresses(dealers).length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">No dealer locations yet — import dealers via SAP Sync first.</div>
            )}
          </div>
          <DialogFooter><Button onClick={saveAssign} className="bg-[#0055FF] hover:bg-[#0044CC]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!managerFor} onOpenChange={o=>{ if(!o) setManagerFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manager scope — {managerFor?.email}</DialogTitle></DialogHeader>
          <div>
            <Label>Manager tier</Label>
            <Select value={managerTier} onValueChange={setManagerTier}>
              <SelectTrigger><SelectValue placeholder="Pick tier" /></SelectTrigger>
              <SelectContent>
                {MANAGER_TIERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {managerTier && managerTier !== "director" && (
            <div>
              <Label>Subordinates</Label>
              <p className="text-xs text-slate-500 mb-1">Sees and can approve orders from these people (and, transitively, anyone under a manager listed here).</p>
              <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
                {users.filter(u => (u.role === "sales" || u.role === "manager") && u.user_id !== managerFor?.user_id).map(u => {
                  const checked = managerSubIds.includes(u.user_id);
                  return (
                    <label key={u.user_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          setManagerSubIds(v ? [...managerSubIds, u.user_id] : managerSubIds.filter(id => id !== u.user_id));
                        }}
                      />
                      <span className="capitalize text-slate-500">{u.role}</span> · {u.name} ({u.email})
                    </label>
                  );
                })}
                {users.filter(u => (u.role === "sales" || u.role === "manager") && u.user_id !== managerFor?.user_id).length === 0 && (
                  <div className="px-3 py-4 text-xs text-slate-400 text-center">No sales or manager users to assign yet.</div>
                )}
              </div>
            </div>
          )}
          {managerTier === "director" && (
            <p className="text-xs text-slate-500">Directors see every dealer and PO — no subordinate assignment needed.</p>
          )}
          <DialogFooter><Button onClick={saveManager} className="bg-[#0055FF] hover:bg-[#0044CC]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
